/**
 * useIntents — fetches canonical intents from public.intents table.
 * Returns active intents grouped by group_key, sorted by sort_order.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Intent {
  id: string;
  key: string;
  label_sv: string;
  group_key: string;
  sort_order: number;
}

export interface IntentGroup {
  key: string;
  label: string;
  icon: string;
  intents: Intent[];
}

const GROUP_META: Record<string, { label: string; icon: string }> = {
  natur_rorelse:    { label: "Natur & Rörelse",    icon: "🌿" },
  stillhet_narvaro: { label: "Stillhet & Närvaro", icon: "🧘" },
  mat_smaker:       { label: "Mat & Smaker",       icon: "🍞" },
  kultur_larande:   { label: "Kultur & Lärande",   icon: "🎨" },
  familj_socialt:   { label: "Socialt & Familj",   icon: "👨‍👩‍👧‍👦" },
};

export function useIntents() {
  return useQuery({
    queryKey: ["intents-registry"],
    queryFn: async (): Promise<IntentGroup[]> => {
      const { data, error } = await supabase
        .from("intents")
        .select("id, key, label_sv, group_key, sort_order")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (error) throw error;

      // Group by group_key
      const grouped = new Map<string, Intent[]>();
      for (const row of data ?? []) {
        const list = grouped.get(row.group_key) ?? [];
        list.push(row);
        grouped.set(row.group_key, list);
      }

      const groupOrder = Object.keys(GROUP_META);
      return groupOrder
        .filter((gk) => grouped.has(gk))
        .map((gk) => ({
          key: gk,
          label: GROUP_META[gk]?.label ?? gk,
          icon: GROUP_META[gk]?.icon ?? "📌",
          intents: grouped.get(gk)!,
        }));
    },
    staleTime: 10 * 60 * 1000, // 10 min
  });
}

/**
 * usePlaceIntents — fetches selected intents for a place via place_intents join.
 * Returns intent keys (strings).
 */
export function usePlaceIntents(placeId: string | undefined) {
  return useQuery({
    queryKey: ["place-intents", placeId],
    enabled: !!placeId,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("place_intents")
        .select("intent_id, intents!inner(key)")
        .eq("place_id", placeId!);

      if (error) throw error;

      // Extract keys - intents is joined
      return (data ?? []).map((row: any) => row.intents?.key).filter(Boolean);
    },
  });
}
